import { Badge } from '@chakra-ui/react';
import type { SearchAffiliateUsersResponse } from '@fluenceur/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecipientOptionView } from '@/components/recipient-picker';
import { useSearchAffiliateUsers } from './use-search-affiliate-users';

/** One affiliate search row (carries the `userId` as `id`, plus program memberships). */
export type AffiliateRecipient = SearchAffiliateUsersResponse['data'][0];

interface UseAffiliateRecipientSearchOptions {
  /**
   * i18n namespace for the copy this hook resolves:
   * `<prefix>.minCharacters | .error | .noResults` (empty-state) and, when
   * `withBadge`, `<prefix>.activeInPrograms` (i18next `_one`/`_other` plurals).
   */
  i18nPrefix: string;
  /** Render an "active in N programs" badge on each option (Add Affiliate only). */
  withBadge?: boolean;
}

interface AffiliateRecipientSearch {
  /** Raw rows, so a caller can look one up by id on selection. */
  affiliates: AffiliateRecipient[];
  /** Rows mapped to the picker's neutral view. */
  options: RecipientOptionView[];
  /** Single empty-state string the picker shows when the list is empty. */
  emptyHint: string;
  isLoading: boolean;
  isFetching: boolean;
}

// Partial, deliberately loose heuristic to GATE the search off email-shaped
// input (the endpoint matches names/emails; a half-typed address with `+`/`.`
// would fire noisy partial searches). This is NOT the picker's complete
// `isValidEmail` — a complete email routes to the raw-email row instead. Kept
// separate on purpose so the two are never merged.
const looksLikeEmail = (value: string): boolean =>
  value.includes('@') || (/[+.]/.test(value) && /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*$/.test(value));

/**
 * Shared affiliate-recipient search for the two flows that pick an existing
 * affiliate OR type a raw email through the RecipientPicker: the invitation
 * wizard's smart-input step and the Add Affiliate modal. Both keep the affiliate
 * search as their source (strategy B — the endpoint returns the `userId` an
 * existing-user invite needs); only the i18n namespace and the badge differ.
 * The picker itself owns the raw-email affordance.
 */
export const useAffiliateRecipientSearch = (
  search: string,
  { i18nPrefix, withBadge = false }: UseAffiliateRecipientSearchOptions
): AffiliateRecipientSearch => {
  const { t } = useTranslation();
  const trimmed = search.trim();
  const searchQuery = !looksLikeEmail(trimmed) && trimmed.length >= 2 ? trimmed : '';

  const {
    data: searchResults,
    isLoading,
    isFetching,
    error,
  } = useSearchAffiliateUsers(searchQuery);

  const affiliates = useMemo(() => searchResults?.data ?? [], [searchResults]);

  const options = useMemo<RecipientOptionView[]>(
    () =>
      affiliates.map((affiliate) => ({
        id: affiliate.id,
        displayName: affiliate.name,
        subLabel: affiliate.email,
        avatarUrl: affiliate.avatar?.fileUrl ?? null,
        email: affiliate.email,
        badge: withBadge ? (
          <Badge colorPalette="primary" size="xs">
            {t(`${i18nPrefix}.activeInPrograms`, { count: affiliate.programs.length })}
          </Badge>
        ) : undefined,
      })),
    [affiliates, withBadge, i18nPrefix, t]
  );

  const emptyHint =
    trimmed.length < 2
      ? t(`${i18nPrefix}.minCharacters`)
      : error
        ? t(`${i18nPrefix}.error`, { message: error.message })
        : t(`${i18nPrefix}.noResults`, { query: search });

  return { affiliates, options, emptyHint, isLoading, isFetching };
};
